/* Maps backend error keys to localized, user-facing messages. */
export const ERROR_TRANSLATIONS: Record<string, string> = {
    // Banner related errors
    'NAME_NOT_UNIQUE': 'שם הבאנר כבר קיים במערכת',
    'URL_NOT_UNIQUE': 'כתובת הבאנר כבר קיימת במערכת',
    'BANNER_NOT_FOUND': 'הבאנר לא נמצא',
    'BANNER_ALREADY_EXISTS': 'הבאנר כבר קיים',
    'ADMIN_ALREADY_REGISTERED': 'שם המשתמש כבר קיים במערכת',

    // File related errors
    'FILE_NOT_FOUND': 'הקובץ לא נמצא',
    'INVALID_FILE_TYPE': 'סוג הקובץ אינו נתמך',
    'FILE_TOO_LARGE': 'הקובץ גדול מדי',
    'FILE_UPLOAD_FAILED': 'העלאת הקובץ נכשלה',
    // POST /professionals/upload (the supplier's picture)
    'EXTENSION_NOT_ALLOWED': 'סוג הקובץ אינו נתמך – אפשר להעלות תמונה מסוג PNG, JPG, GIF, WEBP, BMP או SVG',
    'SINGLE_FILE': 'יש לבחור קובץ אחד שאינו ריק',
    'UPLOAD_FAILED': 'העלאת הקובץ נכשלה',
    'PROFESSIONAL_NOT_FOUND': 'הספק לא נמצא',

    // Validation errors
    'REQUIRED_FIELD': 'שדה חובה',
    'INVALID_URL': 'כתובת אינה תקינה',
    'INVALID_EMAIL': 'כתובת אימייל אינה תקינה',
    'INVALID_DATA': 'הנתונים שהוזנו אינם תקינים',
    'INVALID_FORMAT': 'פורמט לא תקין',
    'INVALID_PASSWORD': 'הסיסמה אינה תקינה: יש להשתמש באותיות באנגלית, ספרות וסימנים, ללא רווחים, באורך של 8 עד 255 תווים, ולא בסיסמת ברירת המחדל.',
    'INVALID_USERNAME': 'שם המשתמש אינו תקין: אותיות באנגלית ומספרים בלבד, ללא רווחים, ולא ספרות בלבד',
    'INVALID_ID_NUMBER': 'מספר תעודת הזהות אינו תקין',
    'INVALID_PHONE': 'מספר טלפון אינו תקין',

    // Authentication errors
    'UNAUTHORIZED': 'אין הרשאה לפעולה זו',
    'UNABLE_TO_DELETE_SELF': 'לא ניתן למחוק את המשתמש המחובר',
    'SESSION_EXPIRED': 'החיבור הסתיים – יש להתחבר מחדש',
    'INVALID_CREDENTIALS': 'שם המשתמש או הסיסמה שגויים',
    'TOO_MANY_ATTEMPTS': 'יותר מדי ניסיונות, נסו שוב מאוחר יותר',
    'TOKEN_EXPIRED': 'תוקף הטוקן פג',
    'ACCESS_DENIED': 'הגישה נדחתה',

    // Projects, users and professionals
    'DOCUMENT_NOT_GENERATED': 'המסמך אינו זמין עדיין',
    'PROFESSIONAL_IN_USE': 'הספק משויך לבניינים – כמועמד, בסקר או כספק שנבחר',
    'APARTMENT_NUMBER_REQUIRED': 'יש לבחור מספר דירה',
    'INVALID_APARTMENT_NUMBER': 'הדירה לא נמצאת ברשימת הדירות של הבניין',
    'APARTMENT_ALREADY_TAKEN': 'הדירה הזו כבר תפוסה',
    'DUPLICATE_PHONE': 'מספר הטלפון כבר רשום במערכת',
    'PROJECT_NOT_FOUND': 'הפרויקט לא נמצא',
    'USER_NOT_FOUND': 'המשתמש לא נמצא',

    // General errors
    'INTERNAL_SERVER_ERROR': 'שגיאה פנימית בשרת',
    'NETWORK_ERROR': 'שגיאת רשת',
    'TIMEOUT': 'זמן התגובה פג',
    'UNKNOWN_ERROR': 'שגיאה לא ידועה',

    // Database errors
    'DATABASE_CONNECTION_ERROR': 'שגיאה בחיבור למסד הנתונים',
    'CONSTRAINT_VIOLATION': 'הפרת אילוץ במסד הנתונים',
    'DUPLICATE_ENTRY': 'רשומה כפולה',

    // Permission errors
    'INSUFFICIENT_PERMISSIONS': 'אין הרשאות מספיקות',
    'ROLE_REQUIRED': 'נדרש תפקיד מתאים',

    // Rate limiting
    'RATE_LIMIT_EXCEEDED': 'חרגת ממגבלת הבקשות',
    'TOO_MANY_REQUESTS': 'יותר מדי בקשות'
}

export function translateError(errorKey: unknown): string {
    if (typeof errorKey !== 'string') return ERROR_TRANSLATIONS.UNKNOWN_ERROR || 'שגיאה לא ידועה'
    return ERROR_TRANSLATIONS[errorKey] || errorKey
}

export function translateErrorObject(errors: Record<string, unknown>): string {
    if (!errors || typeof errors !== 'object') return ''

    const messages: string[] = []

    const collectMessages = (value: unknown): void => {
        if (value == null) return
        if (Array.isArray(value)) {
            value.forEach(collectMessages)
            return
        }
        if (typeof value === 'object') {
            Object.values(value as Record<string, unknown>).forEach(collectMessages)
            return
        }
        messages.push(translateError(String(value)))
    }

    collectMessages(errors)

    return messages.join('\n')
}

/** Business error codes are UPPER_SNAKE_CASE (e.g. `PROFESSIONAL_IN_USE`); anything else is a generic status text. */
const ERROR_CODE_PATTERN = /^[A-Z][A-Z0-9_]+$/

/**
 * The business error code of a failed API call: `meta.description` (the API's error format), or the first code in a
 * validation `errors` list. Undefined when the response carries no code.
 */
export function getApiErrorCode(error: unknown): string | undefined {
    const data = (error as any)?.response?.data ?? (error as any)?.data
    const description = data?.meta?.description
    if (typeof description === 'string' && ERROR_CODE_PATTERN.test(description)) return description
    if (data?.errors && typeof data.errors === 'object') {
        for (const value of Object.values(data.errors)) {
            const first = Array.isArray(value) ? value[0] : value
            if (typeof first === 'string' && ERROR_CODE_PATTERN.test(first)) return first
        }
    }
    if (typeof data?.message === 'string' && ERROR_CODE_PATTERN.test(data.message)) return data.message
    if (typeof data === 'string' && ERROR_CODE_PATTERN.test(data.trim())) return data.trim()
    return undefined
}

/**
 * A Hebrew message for a failed API call: the translation of its business code when there is one, all the messages
 * of a validation `errors` list, or `fallback`.
 */
export function getApiErrorMessage(error: unknown, fallback: string): string {
    const data = (error as any)?.response?.data ?? (error as any)?.data
    const code = getApiErrorCode(error)
    if (code && ERROR_TRANSLATIONS[code]) return ERROR_TRANSLATIONS[code]
    if (data?.errors && typeof data.errors === 'object') {
        const messages = translateErrorObject(data.errors)
        if (messages) return messages
    }
    return code ? `${fallback} (${code})` : fallback
}

/**
 * Reads an error body that arrived as a Blob (requests made with `responseType: 'blob'`, e.g. downloads) back into
 * JSON, so `getApiErrorCode` can see its `meta.description`. Leaves any other error untouched.
 */
export async function parseBlobErrorBody(error: unknown): Promise<void> {
    const response = (error as any)?.response
    const body = response?.data
    if (!response || typeof Blob === 'undefined' || !(body instanceof Blob)) return
    try {
        const text = await body.text()
        response.data = text ? JSON.parse(text) : null
    } catch {
        // Not JSON: keep the Blob.
    }
}
